// database.js
import { collection, doc, getDoc, setDoc, getDocs, deleteDoc, writeBatch, updateDoc } from "firebase/firestore";
import { initFirebase } from "./firebase";

export const ACCESS_LEVELS = {
  ADMIN: 'admin',
  BETA_TESTER: 'beta',
  WAITLIST: 'waitlist'
};

const GLOBAL_COURSE_CONFIG = {
  ALLOW_OVERWRITE: true,
  VERSION: 'two-phase-v1'
};

// Default preferences helper function
export const getDefaultPreferences = () => {
  return {
    collections: ['Currently Learning', 'Completed', 'Want to Learn'],
    categories: ['Programming', 'Music', 'Design', 'Business', 'Technology', 'Language'],
    defaultView: 'grid'
  };
};

const ensureFirebase = async () => {
  if (typeof window === 'undefined') {
    console.warn("Firebase operations are not available during static build");
    return null;
  }
  try {
    const { db } = await initFirebase();
    console.log("Firebase DB initialized:", !!db, "Environment:", process.env.NODE_ENV);
    return db;
  } catch (error) {
    console.error("Error initializing Firebase:", error);
    return null;
  }
};

const normalizeCourseId = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/['"""]/g, '')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
};

const saveCourse = async (userId, courseId, courseData) => {
  const db = await ensureFirebase();
  if (!db) return null;

  try {
    const userCoursesRef = collection(db, "Users", userId, "customCourses");
    const normalizedId = courseId ? normalizeCourseId(courseId) : doc(userCoursesRef).id;
    const courseRef = doc(userCoursesRef, normalizedId);
    
    const courseDataToSave = {
      title: courseData.title || '',
      description: courseData.description || '',
      createdAt: new Date(),
      stages: (courseData.stages || []).map(stage => ({
        stageNumber: stage.stageNumber || 1,
        stageName: stage.stageName || 'Stage 1',
        lessons: (stage.lessons || []).map(lesson => ({
          lessonNumber: lesson.lessonNumber || 1,
          lessonName: lesson.lessonName || 'Lesson 1',
          description: lesson.description || '',
          completed: lesson.completed || false, // Save completion status
          videos: (lesson.videos || []).map(video => ({
            id: video.id || '',
            title: video.title || '',
            thumbnail: video.thumbnail || '',
            url: video.url || (video.id ? `https://www.youtube.com/watch?v=${video.id}` : '')
          }))
        }))
      }))
    };

    await setDoc(courseRef, courseDataToSave, { merge: true });
    return normalizedId;
  } catch (error) {
    console.error("Error saving course:", error);
    throw error;
  }
};

const updateCourse = async (userId, courseId, courseData) => {
  const db = await ensureFirebase();
  if (!db) return null;
  
  try {
    const normalizedId = normalizeCourseId(courseId);
    const courseRef = doc(db, "Users", userId, "customCourses", normalizedId);
    
    // Get the current course data to ensure we're not overwriting anything
    const currentDoc = await getDoc(courseRef);
    if (!currentDoc.exists()) {
      console.error("Cannot update non-existent course");
      throw new Error("Course not found");
    }
    
    // Use updateDoc instead of setDoc for partial updates
    await updateDoc(courseRef, courseData);
    console.log("Course updated successfully:", normalizedId);
    return normalizedId;
  } catch (error) {
    console.error("Error updating course:", error);
    throw error;
  }
};

const getUserPreferences = async (userId) => {
  const db = await ensureFirebase();
  if (!db) return getDefaultPreferences();
  
  try {
    const prefDoc = doc(db, "Users", userId, "preferences", "userPrefs");
    const docSnap = await getDoc(prefDoc);
    
    if (docSnap.exists()) {
      return { ...getDefaultPreferences(), ...docSnap.data() };
    }
    return getDefaultPreferences();
  } catch (error) {
    console.error("Error fetching user preferences:", error);
    return getDefaultPreferences();
  }
};

const saveUserPreferences = async (userId, prefsData) => {
  const db = await ensureFirebase();
  if (!db) return null;
  
  try {
    const prefDoc = doc(db, "Users", userId, "preferences", "userPrefs");
    
    // Merge with existing preferences to avoid overwriting fields not included in prefsData
    await setDoc(prefDoc, prefsData, { merge: true });
    return true;
  } catch (error) {
    console.error("Error saving user preferences:", error);
    throw error;
  }
};

const saveCourseToCoursesCollection = async (courseId, courseData) => {
  const db = await ensureFirebase();
  if (!db) return null;

  try {
    const coursesRef = collection(db, "Courses");
    const normalizedId = normalizeCourseId(courseId);
    const courseRef = doc(coursesRef, normalizedId);

    // Check if document exists
    const docSnap = await getDoc(courseRef);
    
    if (docSnap.exists() && !GLOBAL_COURSE_CONFIG.ALLOW_OVERWRITE) {
      console.warn(`Course ${normalizedId} already exists. Overwrite is disabled.`);
      return courseRef.id;
    }

    const courseDataToSave = {
      title: courseData.title || '',
      description: courseData.description || '',
      createdAt: new Date(),
      generationVersion: GLOBAL_COURSE_CONFIG.VERSION, // Optional: track version
      stages: courseData.stages.map(stage => ({
        stageNumber: stage.stageNumber,
        stageName: stage.stageName,
        lessons: stage.lessons.map(lesson => ({
          lessonNumber: lesson.lessonNumber,
          lessonName: lesson.lessonName,
          description: lesson.description || '',
          videos: lesson.videos.map(video => ({
            id: video.id,
            title: video.title || '',
            thumbnail: video.thumbnail || '',
            url: video.url || `https://www.youtube.com/watch?v=${video.id}`
          }))
        }))
      }))
    };

    if (docSnap.exists()) {
      console.warn(`Overwriting existing course: ${normalizedId}`);
    }

    await setDoc(courseRef, courseDataToSave, { merge: false }); // merge: false ensures complete overwrite
    return courseRef.id;
  } catch (error) {
    console.error("Error saving course to Courses collection:", error);
    throw error;
  }
};

const getSavedCourse = async (userId, courseId) => {
  try {
    const db = await ensureFirebase();
    if (!db) return null;

    const normalizedId = normalizeCourseId(courseId);
    console.log("Getting saved course with normalized ID:", normalizedId);
    
    const courseDoc = doc(db, "Users", userId, "customCourses", normalizedId);
    const docSnap = await getDoc(courseDoc);
    
    if (docSnap.exists()) {
      console.log("Course document exists in Firebase");
      const courseData = { id: docSnap.id, ...docSnap.data() };
      
      // Safely log the structure without potentially causing errors
      console.log("Course data structure:", {
        id: courseData.id,
        title: courseData.title || 'No title',
        hasStages: Boolean(courseData.stages && Array.isArray(courseData.stages)),
        stagesCount: courseData.stages && Array.isArray(courseData.stages) ? courseData.stages.length : 0,
        hasSteps: Boolean(courseData.steps && Array.isArray(courseData.steps)),
        stepsCount: courseData.steps && Array.isArray(courseData.steps) ? courseData.steps.length : 0
      });
      
      return courseData;
    }
    
    console.log("Course document does NOT exist in Firebase");
    return null;
  } catch (error) {
    console.error("Error fetching course:", error);
    throw error;
  }
};

const getSavedCourses = async (userId) => {
  const db = await ensureFirebase();
  if (!db) return [];

  try {
    const userCoursesRef = collection(db, "Users", userId, "customCourses");
    const snapshot = await getDocs(userCoursesRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error retrieving saved courses:", error);
    throw error;
  }
};

const getCourseById = async (courseId) => {
  const db = await ensureFirebase();
  if (!db) return null;

  try {
    const normalizedId = normalizeCourseId(courseId);
    const courseDoc = doc(db, "Courses", normalizedId);
    const docSnap = await getDoc(courseDoc);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error("Error fetching course:", error);
    throw error;
  }
};

const deleteCourse = async (userId, courseId) => {
  const db = await ensureFirebase();
  if (!db) return null;

  try {
    const normalizedId = normalizeCourseId(courseId);
    console.log(`Deleting course with normalized ID: ${normalizedId}`);
    
    const courseRef = doc(db, "Users", userId, "customCourses", normalizedId);
    
    // Check if it exists before trying to delete
    const docSnap = await getDoc(courseRef);
    if (!docSnap.exists()) {
      console.warn(`Cannot delete: Course ${normalizedId} not found in user's collection`);
      return false;
    }
    
    await deleteDoc(courseRef);
    console.log(`Deletion operation completed for: ${normalizedId}`);
    
    // Verify deletion was successful
    const verifySnap = await getDoc(courseRef);
    if (verifySnap.exists()) {
      console.error(`Deletion verification failed: Course ${normalizedId} still exists`);
      return false;
    }
    
    console.log(`Verified: Course ${normalizedId} successfully deleted`);
    return true;
  } catch (error) {
    console.error(`Error in deleteCourse for ${courseId}:`, error);
    throw error;
  }
};

const addToWaitlist = async (email) => {
  const db = await ensureFirebase();
  if (!db) return null;

  try {
    const normalizedEmail = email.toLowerCase().trim();
    const batch = writeBatch(db);

    // Create main user access document
    const accessRef = doc(db, "AccessControl", normalizedEmail);
    batch.set(accessRef, {
      email: normalizedEmail,
      createdAt: new Date(),
      lastUpdated: new Date(),
      currentStatus: ACCESS_LEVELS.WAITLIST
    }, { merge: true });

    // Add waitlist details
    const waitlistRef = doc(accessRef, "status", ACCESS_LEVELS.WAITLIST);
    batch.set(waitlistRef, {
      joinedAt: new Date(),
      status: 'pending',
      platform: {
        origin: window?.location?.origin || 'unknown',
        userAgent: window?.navigator?.userAgent || 'unknown'
      }
    });

    await batch.commit();
    
    return { success: true };
  } catch (error) {
    console.error("Error adding to waitlist:", error);
    throw error;
  }
};

const checkUserAccess = async ({ uid, email }) => {
  const db = await ensureFirebase();
  if (!db) return null;

  try {
    if (email) {
      const normalizedEmail = email.toLowerCase().trim();
      console.log('Checking access with email:', normalizedEmail);
      const accessRef = doc(db, "AccessControl", normalizedEmail);
      const accessDoc = await getDoc(accessRef);

      if (accessDoc.exists()) {
        const currentStatus = accessDoc.data().currentStatus;
        console.log('Email access check result:', { exists: true, currentStatus });
        return { exists: true, currentStatus };
      }
    }

    console.log('No access found');
    return { exists: false };
  } catch (error) {
    console.error("Error checking user access:", error);
    throw error;
  }
};

// Export everything else
export {
  GLOBAL_COURSE_CONFIG,
  normalizeCourseId,
  saveCourse,
  saveCourseToCoursesCollection,
  getSavedCourse,
  getSavedCourses,
  getCourseById,
  deleteCourse,
  addToWaitlist,
  checkUserAccess,
  updateCourse,
  getUserPreferences,
  saveUserPreferences
};
